import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AuthShowcase, type ShowcaseCopy } from './AuthShowcase'
import { Logo } from './Logo'

/**
 * The page shape shared by sign-in and join-the-network: the picture on the left, the form on the
 * right. Below the `xl` breakpoint the picture is dropped - a phone has no room for a second
 * column - and its one sentence moves under the form instead, so the pitch is never lost.
 *
 * @param wide      for the multi-field join forms, which need more than a sign-in column does
 * @param wider     wider still, for a page laid out in two columns (the role picker)
 * @param headerEnd what sits opposite the logo; a tagline pill unless the page has something better
 * @param showcase  what the picture says, for a page that is not the member sign-in (the ops console)
 * @param blurb     the line under the form on small screens, in place of the picture
 */
export function AuthLayout({
  children,
  wide = false,
  wider = false,
  headerEnd,
  showcase,
  blurb,
}: {
  children: ReactNode
  wide?: boolean
  wider?: boolean
  headerEnd?: ReactNode
  showcase?: ShowcaseCopy
  blurb?: ReactNode
}) {
  return (
    <div className="min-h-screen bg-sun-hero xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:gap-6 xl:p-3">
      <aside className="hidden xl:block">
        <div className="sticky top-3 h-[calc(100vh-1.5rem)]">
          <AuthShowcase {...showcase} />
        </div>
      </aside>

      <main className="flex min-h-screen flex-col px-5 py-4 sm:px-10 xl:min-h-0 xl:px-8">
        <header className="flex items-center justify-between gap-3">
          <Link to="/" aria-label="YellowDots home" className="min-w-0">
            <Logo size="page" />
          </Link>
          {headerEnd ?? (
            <span className="hidden shrink-0 rounded-full bg-dot-50 px-3 py-1 text-xs font-semibold text-dot-700 sm:inline">
              Learning, discovered on a map
            </span>
          )}
        </header>

        <div className={`mx-auto my-auto w-full py-4 ${wider ? 'max-w-2xl' : wide ? 'max-w-xl' : 'max-w-md'}`}>{children}</div>

        <p className="mx-auto max-w-sm text-center text-sm leading-relaxed text-ink-500 xl:hidden">
          {blurb ?? (
            <>
              <span className="font-semibold text-ink-900">Discover What&apos;s Next, Around You.</span> Find
              trusted tutors, coaches, trainers, schools, colleges and jobs near you.
            </>
          )}
        </p>
      </main>
    </div>
  )
}
